import {isFeatureEnabled} from 'feature-flags';

import {useGetOfferAlternativesQuery} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';

import {useQueryState} from 'shared/web';

import {getAlternativeRights, type OfferAlternativePermissions} from './getAlternativeRights';

const ACTIVE_ALTERNATIVE_QUERY_PARAM = 'offerAlternative';

const byLetter = (a: {letter: string}, b: {letter: string}) =>
  a.letter.length - b.letter.length || a.letter.localeCompare(b.letter);

/**
 * Offer alternatives of the Selling section, part of the new-vehicle catalogue. Catalogue flag
 * off, or a Business Case without alternatives, yields `hasAlternatives: false` and the section
 * renders exactly as before.
 *
 * `permissions` are the seller's deal rights. The backend flags reflect only the Business Case
 * state, so every alternative carries the rights the current user really has (see
 * `getAlternativeRights`); adding one needs ADD_VEHICLE_TO_SELL, like adding a sale vehicle.
 */
export function useOfferAlternativesSelling(
  businessCaseId: string,
  permissions: OfferAlternativePermissions
) {
  const isEnabled = isFeatureEnabled(featureFlags.NEW_VEHICLE_CATALOGUE);
  const {data: offerAlternatives} = useGetOfferAlternativesQuery(
    {businessCaseId},
    {skip: !isEnabled}
  );
  const [activeId, setActiveId] = useQueryState(ACTIVE_ALTERNATIVE_QUERY_PARAM);

  const canAdd = isEnabled && offerAlternatives?.canAdd === true && permissions.canAddVehicleToSell;
  const alternatives = (offerAlternatives?.alternatives ?? [])
    .map((alternative) => getAlternativeRights(alternative, permissions))
    .sort(byLetter);
  // The open tab falls back to the deal vehicle, then to the first alternative.
  const activeAlternative =
    alternatives.find((alternative) => alternative.id === activeId) ??
    alternatives.find((alternative) => alternative.isApplied) ??
    alternatives[0];

  return {
    offerAlternatives:
      isEnabled && offerAlternatives ? {...offerAlternatives, canAdd, alternatives} : undefined,
    alternatives,
    activeAlternative,
    hasAlternatives: isEnabled && alternatives.length > 0,
    canAdd,
    setActiveId,
  };
}
